// VAST Ads Mixin for media-chrome <hls-video>
// Adds preroll VAST support using @dailymotion/vast-client
//
// Architecture Notes:
// -------------------
// This mixin handles VAST preroll ads with browser-specific strategies:
//
// 1. Ad Prefetching: Ads are fetched immediately on connectedCallback to ensure
//    they're ready when play is triggered. This is essential for Safari's
//    user gesture requirements.
//
// 2. Safari Strategy: Uses MEDIA_PLAY_REQUEST event from media-chrome, which
//    fires BEFORE play() is called. This preserves the user gesture context,
//    allowing synchronous ad video playback without NotAllowedError.
//
// 3. Chrome Strategy: Uses play() method override as primary mechanism, with
//    a 'play' event listener as safety net. Chrome/HLS.js can sometimes bypass
//    the play() override, so we catch those cases and pause + retry.
//
// 4. Poster Locking: The poster slot is forced visible while ads load to
//    prevent the black flash that occurs when video hasn't loaded yet.

import { VASTClient, VASTTracker } from '@dailymotion/vast-client';
import { isSafari } from './utils/browser-detection.js';

// media-chrome event name for play requests (from MEDIA_PLAY_REQUEST)
const MEDIA_PLAY_REQUEST = 'mediaplayrequest';

/**
 * Mixin that enhances a video element with VAST preroll ad support.
 * Usage:
 *   class HlsVideoWithAds extends VASTAdsMixin(HlsVideo) {}
 *   customElements.define('hls-video-ads', HlsVideoWithAds);
 *
 * Attributes:
 *   vast-src: URL to VAST XML
 *   ad-timeout: optional timeout (ms) before failing ad and playing content
 *   skip-delay: optional time (ms) before skip button appears (default: 5000)
 */
export function VASTAdsMixin(Base) {
  return class VASTAds extends Base {
    static get observedAttributes() {
      return (super.observedAttributes || []).concat([
        'vast-src',
        'ad-timeout',
        'skip-delay',
      ]);
    }

    constructor() {
      super();

      // Only initialize VAST-related state if vast-src attribute is present
      if (!this.hasAttribute('vast-src')) {
        return;
      }

      this._initializeVastState();
    }

    /**
     * Initialize all VAST-related state and bindings.
     * Called from constructor if vast-src is present, or lazily when vast-src is set dynamically.
     */
    _initializeVastState() {
      // Prevent double initialization
      if (this._vastInitialized) return;
      this._vastInitialized = true;

      // Core ad state
      this._adsClient = new VASTClient();
      this._adsPlayed = false;
      this._adPlayInProgress = false;
      this._adPlayPromise = null;
      this._adPrepared = false;
      this._adPreparePromise = null;
      this._preparedAd = null;
      this._controllerEl = null;
      this._isSafari = isSafari();

      // Poster management
      this._posterSlot = null;

      // Original video state (for resuming after ad)
      this._originalSrc = null;
      this._originalCurrentTime = 0;
      this._originalMuted = false;

      // Timeouts
      this._timeoutId = null;
      this._adLoadTimeout = null;
      this._skipButtonTimeout = null;

      // Ad playback state
      this._skipDelay = 5000;
      this._skipAllowed = false;
      this._quartilesFired = {};
      this._googleWhyThisAdIcon = null;
      this._onGoogleWhyThisAdClick = this._onGoogleWhyThisAdClick?.bind(this) ||
        ((_e) => {});

      // Bind ad event handlers for reliable add/remove
      this._onAdCanPlay = this._onAdCanPlay.bind(this);
      this._onAdEnded = this._onAdEnded.bind(this);
      this._onAdError = this._onAdError.bind(this);
      this._onAdPlaying = this._onAdPlaying.bind(this);
      this._onAdLoadedMetadata = this._onAdLoadedMetadata.bind(this);
      this._onAdProgress = this._onAdProgress.bind(this);
      this._onAdVolumeChange = this._onAdVolumeChange.bind(this);
      this._onAdPause = this._onAdPause.bind(this);
      this._onAdPlay = this._onAdPlay.bind(this);
      this._onAdFullscreenChange = this._onAdFullscreenChange.bind(this);
      this._onAdClick = this._onAdClick.bind(this);

      // Create overlay DOM elements (but don't attach to DOM yet)
      this._createAdOverlay();
    }

    connectedCallback() {
      super.connectedCallback?.();

      // Skip all VAST handling if not initialized
      if (!this._vastInitialized) {
        return;
      }

      this._controllerEl = this.closest('media-controller') || null;

      // Note: Overlay is attached to DOM only when we're about to show an ad (in _showAdOverlay)
      // This prevents issues when VAST response is empty

      if (this._vastSrc) {
        // Prefetch ad data (don't lock poster yet - wait until we know we have an ad)
        this._prefetchAd();

        // Safari: intercept MEDIA_PLAY_REQUEST to preserve user gesture context
        if (this._isSafari) {
          this._onPlayRequest = this._handlePlayRequest.bind(this);
          const target = this._controllerEl || this;
          target.addEventListener(
            MEDIA_PLAY_REQUEST,
            this._onPlayRequest,
            { capture: true },
          );
        }

        // Chrome: catch play events that bypass our play() override
        if (!this._isSafari) {
          this._onPlayEvent = this._handlePlayEvent.bind(this);
          this.addEventListener('play', this._onPlayEvent);
        }
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback?.();

      // Skip all VAST cleanup if not initialized
      if (!this._vastInitialized) {
        return;
      }

      if (this._onPlayRequest) {
        const target = this._controllerEl || this;
        target.removeEventListener(
          MEDIA_PLAY_REQUEST,
          this._onPlayRequest,
          { capture: true },
        );
      }
      if (this._onPlayEvent) {
        this.removeEventListener('play', this._onPlayEvent);
      }

      // Clear pending timeouts to prevent memory leaks
      if (this._timeoutId) {
        clearTimeout(this._timeoutId);
        this._timeoutId = null;
      }
      if (this._adLoadTimeout) {
        clearTimeout(this._adLoadTimeout);
        this._adLoadTimeout = null;
      }
      if (this._skipButtonTimeout) {
        clearTimeout(this._skipButtonTimeout);
        this._skipButtonTimeout = null;
      }

      // Cancel references to ongoing ad operations
      this._adPreparePromise = null;
      this._adPlayPromise = null;

      // Clean up any currently playing ad
      if (
        this._adPlayInProgress && this._adOverlay?.style.display === 'block'
      ) {
        this._removeAdVideoListeners?.();
        this._adVideo?.pause();
      }

      this._controllerEl = null;
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (name === 'vast-src') {
        // Lazy initialize if vast-src is set dynamically
        if (newVal && !this._vastInitialized) {
          this._initializeVastState();
          // Set up controller reference if already connected
          if (this.isConnected) {
            this._controllerEl = this.closest('media-controller') || null;
          }
          // Note: Overlay is attached lazily in _showAdOverlay, not here
        }

        this._vastSrc = newVal;
        if (this._vastInitialized) {
          this._adPrepared = false;
          this._preparedAd = null;
          this._adPreparePromise = null;
          if (newVal) {
            // New non-empty VAST source: allow preroll ads to play again
            this._adsPlayed = false;
            this._prefetchAd();
          }
        }
      }
      if (name === 'ad-timeout') this._adTimeout = parseInt(newVal, 10) || 5000;
      if (name === 'skip-delay') this._skipDelay = parseInt(newVal, 10) || 5000;
      super.attributeChangedCallback?.(name, oldVal, newVal);
    }

    // =========================================================================
    // Overlay and UI
    // =========================================================================

    _createAdOverlay() {
      this._adOverlay = document.createElement('div');
      this._adOverlay.id = 'vast-overlay';
      this._adOverlay.style.cssText = `
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        background: rgba(0, 0, 0, 0.8);
        display: none;
        z-index: 1000;
        pointer-events: none;
      `;

      this._adTitleDisplay = document.createElement('div');
      this._adTitleDisplay.id = 'vast-ad-title';
      this._adTitleDisplay.style.cssText = `
        position: absolute;
        top: 20px; left: 20px; right: 20px;
        color: white;
        font-size: 20px;
        font-weight: bold;
        z-index: 1002;
        text-shadow: 0 2px 8px rgba(0,0,0,0.7);
        pointer-events: none;
        display: none;
        overflow: hidden;
        text-overflow: ellipsis;
        min-height: 24px;
        line-height: 1.2;
        padding-bottom: 2px;
      `;

      this._adDescriptionDisplay = document.createElement('div');
      this._adDescriptionDisplay.id = 'vast-ad-description';
      this._adDescriptionDisplay.style.cssText = `
        position: absolute;
        top: 48px; left: 20px; right: 20px;
        color: #e0e0e0;
        font-size: 15px;
        z-index: 1002;
        text-shadow: 0 1px 4px rgba(0,0,0,0.7);
        pointer-events: none;
        display: none;
        overflow: hidden;
        text-overflow: ellipsis;
        min-height: 18px;
        line-height: 1.2;
        padding-bottom: 2px;
      `;

      this._adVideo = document.createElement('video');
      this._adVideo.id = 'vast-ad-video';
      this._adVideo.style.cssText = `
        position: absolute;
        top: 0; left: 0;
        width: 100%; height: 100%;
        object-fit: contain;
        z-index: 1000;
      `;
      this._adVideo.controls = false;
      this._adVideo.playsInline = true;

      this._progressBar = document.createElement('div');
      this._progressBar.id = 'vast-ad-progress-bar';
      this._progressBar.style.cssText = `
        position: absolute;
        bottom: 0; left: 0;
        height: 4px;
        background: #ff0000;
        width: 0%;
        transition: width 0.5s linear;
        z-index: 1001;
      `;

      this._adTimeDisplay = document.createElement('div');
      this._adTimeDisplay.id = 'vast-ad-time-display';
      this._adTimeDisplay.style.cssText = `
        position: absolute;
        bottom: 20px; left: 20px;
        padding: 4px 10px;
        background: rgba(0, 0, 0, 0.7);
        color: white;
        border-radius: 4px;
        font-size: 14px;
        z-index: 1001;
        pointer-events: none;
        min-width: 60px;
        text-align: center;
        display: none;
      `;

      this._skipButton = document.createElement('button');
      this._skipButton.id = 'vast-ad-skip-button';
      this._skipButton.style.cssText = `
        position: absolute;
        bottom: 50px; right: 20px;
        padding: 8px 16px;
        background: rgba(0, 0, 0, 0.7);
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        display: none;
        z-index: 1001;
        pointer-events: auto;
      `;
      this._skipButton.textContent = 'Skip Ad';
      this._skipButton.addEventListener('click', () => this._skipAd());

      this._adOverlay.appendChild(this._adVideo);
      this._adOverlay.appendChild(this._progressBar);
      this._adOverlay.appendChild(this._adTimeDisplay);
      this._adOverlay.appendChild(this._adTitleDisplay);
      this._adOverlay.appendChild(this._adDescriptionDisplay);
      this._adOverlay.appendChild(this._skipButton);
      // Note: Overlay is attached to DOM lazily in _showAdOverlay(), not here
    }

    _showAdOverlay() {
      // Attach overlay to DOM if not already attached (lazy attachment)
      if (this._adOverlay && !this._adOverlay.isConnected) {
        this.appendChild(this._adOverlay);
      }

      this._unlockPoster();
      this._adOverlay.style.display = 'block';
      this._adOverlay.style.pointerEvents = 'auto';
      this._skipButton.style.display = 'none';
      this._progressBar.style.width = '0%';
      this._adTimeDisplay.style.display = 'block';
      this._adTimeDisplay.textContent = '';
      this._adTitleDisplay.style.display = this._adTitleDisplay.textContent
        ? 'block'
        : 'none';
      this._adDescriptionDisplay.style.display =
        this._adDescriptionDisplay.textContent ? 'block' : 'none';
      this._adVideo.style.display = 'block';

      // Render GoogleWhyThisAd icon if present
      if (this._googleWhyThisAdIconData && !this._googleWhyThisAdIcon) {
        this._googleWhyThisAdIcon = document.createElement('img');
        this._googleWhyThisAdIcon.src = this._googleWhyThisAdIconData.image;
        this._googleWhyThisAdIcon.alt = 'Why this ad?';
        this._googleWhyThisAdIcon.style.cssText = `
          position: absolute;
          ${this._googleWhyThisAdIconData.yPosition}: 20px;
          ${this._googleWhyThisAdIconData.xPosition}: 20px;
          width: ${this._googleWhyThisAdIconData.width}px;
          height: ${this._googleWhyThisAdIconData.height}px;
          z-index: 1003;
          cursor: pointer;
          background: transparent;
        `;
        this._googleWhyThisAdIcon.addEventListener(
          'click',
          this._onGoogleWhyThisAdClick,
        );
        this._adOverlay.appendChild(this._googleWhyThisAdIcon);
      }
    }

    _hideAdOverlay() {
      clearTimeout(this._skipButtonTimeout);
      clearTimeout(this._adLoadTimeout);
      this._skipButtonTimeout = null;

      this._adOverlay.style.display = 'none';
      this._adOverlay.style.pointerEvents = 'none';
      this._adVideo.style.display = 'none';
      this._skipButton.style.display = 'none';
      this._progressBar.style.width = '0%';
      this._adTimeDisplay.style.display = 'none';
      this._adTimeDisplay.textContent = '';
      this._adTitleDisplay.style.display = 'none';
      this._adTitleDisplay.textContent = '';
      this._adDescriptionDisplay.style.display = 'none';
      this._adDescriptionDisplay.textContent = '';
      this.style.display = 'block';
      this.style.visibility = 'visible';

      if (this._googleWhyThisAdIcon) {
        this._googleWhyThisAdIcon.removeEventListener(
          'click',
          this._onGoogleWhyThisAdClick,
        );
        this._googleWhyThisAdIcon.remove();
        this._googleWhyThisAdIcon = null;
      }
    }

    _updateProgress() {
      if (!this._adVideo || !this._adVideo.duration) return;
      const progress = (this._adVideo.currentTime / this._adVideo.duration) *
        100;
      this._progressBar.style.width = `${progress}%`;
      this._adTimeDisplay.textContent = `${
        this._formatTime(this._adVideo.currentTime)
      } / ${this._formatTime(this._adVideo.duration)}`;
    }

    _formatTime(time) {
      if (!isFinite(time)) return '0:00';
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60).toString().padStart(2, '0');
      return `${minutes}:${seconds}`;
    }

    // =========================================================================
    // Poster Management
    // =========================================================================

    _unlockPoster() {
      if (this._posterSlot) {
        this._posterSlot.style.removeProperty('display');
        this._posterSlot = null;
      }
    }

    // =========================================================================
    // Ad Playback Entry Points
    // =========================================================================

    _skipAd() {
      this._adTracker?.track('skip');
      this._adResolve?.();
    }

    /**
     * Safari: Intercept MEDIA_PLAY_REQUEST (fires before play()).
     * Preserves user gesture context for ad video playback.
     */
    _handlePlayRequest(event) {
      if (this._adsPlayed || this._adPlayInProgress) {
        return;
      }

      const mediaUrl = this._getAdMediaUrlIfReady();
      if (!mediaUrl) {
        return; // Let normal play proceed
      }

      // Stop the play request from reaching the video element
      event.preventDefault();
      event.stopImmediatePropagation();

      this._startAdPlayback(mediaUrl);
    }

    /**
     * Chrome: Catch play events that bypass our play() override.
     * This happens with HLS.js auto-start and browser quirks.
     */
    _handlePlayEvent() {
      if (this._adsPlayed || this._adPlayInProgress) {
        return;
      }

      // Pause main video immediately
      console.info(
        'VAST: caught play event bypassing override, pausing main video',
      );
      this.pause();

      // If ad is ready, trigger ad flow; otherwise wait for prefetch
      if (this._adPrepared && this._preparedAd) {
        this.play();
      } else if (this._adPreparePromise) {
        this._adPreparePromise.then(() => {
          // Always call play() after prefetch completes - play() handles all cases:
          // - _adsPlayed=true (empty/failed VAST) → super.play()
          // - _preparedAd available → starts ad playback
          // - No media URL → super.play()
          if (!this._adPlayInProgress) {
            this.play();
          }
        });
      }
    }

    /**
     * Override play() to intercept all play attempts and show preroll ad first.
     */
    play() {
      // No VAST configured or ads already played - normal play
      if (!this._vastSrc || this._adsPlayed) {
        return super.play();
      }

      // Ad currently playing - return pending promise (prevents race conditions)
      if (this._adPlayInProgress) {
        return this._adPlayPromise || Promise.resolve();
      }

      const mediaUrl = this._getAdMediaUrlIfReady();
      if (!mediaUrl) {
        // Ad not ready - skip preroll and play normally
        return super.play();
      }

      return this._startAdPlayback(mediaUrl);
    }

    /**
     * Check if ad is ready and return media URL, or mark ads as played and return null.
     */
    _getAdMediaUrlIfReady() {
      if (!this._adPrepared || !this._preparedAd) {
        console.info('VAST: ad not prefetched yet, skipping preroll');
        this._adsPlayed = true;
        this._unlockPoster();
        return null;
      }

      const mediaUrl = this._applyAdMetadata(this._preparedAd);
      if (!mediaUrl) {
        console.info('VAST: no playable ad, skipping preroll');
        this._adsPlayed = true;
        this._unlockPoster();
        return null;
      }

      return mediaUrl;
    }

    /**
     * Core ad playback logic - shared by both Safari and Chrome entry points.
     */
    _startAdPlayback(mediaUrl) {
      this._adPlayInProgress = true;

      // Save original video state for resuming later
      this._originalSrc = this.src;
      this._originalCurrentTime = this.currentTime || 0;
      this._originalMuted = this.muted;

      this.dispatchEvent(new CustomEvent('adsstart'));

      // Create tracker and track impression
      this._adTracker = new VASTTracker(
        this._adsClient,
        this._preparedAd.ad,
        this._preparedAd.creative,
      );
      this._adTracker.trackImpression();

      // Play the ad and handle completion
      this._adPlayPromise = this._playAd(mediaUrl)
        .then(() => {
          this._cleanupAd();
        })
        .catch((err) => {
          console.warn('VAST Ads failed:', err);
          this._cleanupAd();
        })
        .finally(() => {
          this._adPlayInProgress = false;
          this._adPlayPromise = null;
        });

      return this._adPlayPromise;
    }

    // =========================================================================
    // Ad Fetching and Preparation
    // =========================================================================

    _prefetchAd() {
      if (this._adPrepared) {
        return Promise.resolve(this._preparedAd);
      }
      if (this._adPreparePromise) {
        return this._adPreparePromise;
      }

      this._adPreparePromise = this._prepareAdData()
        .then((prepared) => {
          this._preparedAd = prepared;
          this._adPrepared = !!prepared;
          // If no ad available, unlock poster and mark ads as done
          if (!prepared) {
            this._adsPlayed = true;
            this._unlockPoster();
          }
          return prepared;
        })
        .catch((err) => {
          console.warn('VAST: prefetch failed', err);
          this._preparedAd = null;
          this._adPrepared = false;
          // On failure, unlock poster and mark ads as done
          this._adsPlayed = true;
          this._unlockPoster();
          return null;
        })
        .finally(() => {
          this._adPreparePromise = null;
        });

      return this._adPreparePromise;
    }

    _prepareAdData() {
      const timeout = this._adTimeout || 5000;

      const fetchPromise = (async () => {
        console.debug('Fetching VAST from:', this._vastSrc);
        const parsed = await this._adsClient.get(this._vastSrc);
        console.debug('VAST parsed:', parsed);

        if (!parsed.ads || parsed.ads.length === 0) {
          console.info('VAST: no ads found in response');
          return null;
        }

        const ad = parsed.ads[0];
        if (!ad) {
          console.info('VAST: missing ad payload');
          return null;
        }

        const creative = ad.creatives?.find((c) => c.type === 'linear');
        if (!creative) {
          console.warn('VAST: no linear creative found, skipping');
          return null;
        }

        const mediaFile = creative.mediaFiles?.find((f) =>
          f.mimeType?.includes('mp4')
        );
        if (!mediaFile) {
          console.warn('VAST: no suitable media file found, skipping');
          return null;
        }

        return { ad, creative, mediaFile };
      })();

      return Promise.race([
        fetchPromise,
        new Promise((_, reject) => {
          this._timeoutId = setTimeout(
            () => reject(new Error('Ad request timeout')),
            timeout,
          );
        }),
      ]).finally(() => clearTimeout(this._timeoutId));
    }

    _applyAdMetadata(preparedAd) {
      if (!preparedAd) return null;
      const { ad, creative, mediaFile } = preparedAd;

      this._adTitleDisplay.textContent = ad.title || '';
      this._adDescriptionDisplay.textContent = ad.description || '';

      // Only allow skip if skipDelay is explicitly set
      if (creative.skipDelay != null && creative.skipDelay >= 0) {
        this._skipDelay = creative.skipDelay * 1000;
        this._skipAllowed = true;
      } else {
        this._skipAllowed = false;
      }

      // Extract click-through URL
      this._adClickThroughUrl = null;
      const clickThrough = creative.videoClickThroughURLTemplate ||
        creative.videoClicks?.clickThrough ||
        creative.videoClicks?.ClickThrough;
      if (clickThrough) {
        this._adClickThroughUrl = typeof clickThrough === 'string'
          ? { url: clickThrough }
          : clickThrough;
      }

      // Reset quartile tracking
      this._quartilesFired = {
        start: false,
        firstQuartile: false,
        midpoint: false,
        thirdQuartile: false,
      };

      // Extract GoogleWhyThisAd icon data
      this._googleWhyThisAdIconData = null;
      if (creative.icons && Array.isArray(creative.icons)) {
        const gIcon = creative.icons.find((icon) =>
          icon.program === 'GoogleWhyThisAd'
        );
        if (gIcon) {
          this._googleWhyThisAdIconData = {
            image: gIcon.staticResource ||
              (gIcon.staticResources && gIcon.staticResources[0]),
            clickThrough: gIcon.iconClickThroughURLTemplate ||
              gIcon.iconClickThrough ||
              (gIcon.iconClicks && gIcon.iconClicks.iconClickThrough),
            width: gIcon.width || 18,
            height: gIcon.height || 18,
            xPosition: gIcon.xPosition || 'right',
            yPosition: gIcon.yPosition || 'bottom',
          };
        }
      }

      return mediaFile.fileURL;
    }

    // =========================================================================
    // Ad Video Playback
    // =========================================================================

    _playAd(src) {
      return new Promise((resolve, reject) => {
        this._adResolve = resolve;
        this._adReject = reject;

        this._showAdOverlay();

        // Ensure ad video is in DOM
        if (!this._adVideo.isConnected) {
          this._adOverlay.appendChild(this._adVideo);
        }

        // Clean up any previous listeners
        this._removeAdVideoListeners();
        clearTimeout(this._adLoadTimeout);

        // Sync volume/mute state with main video
        this._adVideo.src = src;
        this._adVideo.muted = this.muted ?? false;
        this._adVideo.volume = this.volume ?? 1.0;

        // Add event listeners
        this._adVideo.addEventListener('canplaythrough', this._onAdCanPlay, {
          once: true,
        });
        this._adVideo.addEventListener('ended', this._onAdEnded, {
          once: true,
        });
        this._adVideo.addEventListener('error', this._onAdError, {
          once: true,
        });
        this._adVideo.addEventListener('playing', this._onAdPlaying, {
          once: true,
        });
        this._adVideo.addEventListener(
          'loadedmetadata',
          this._onAdLoadedMetadata,
          { once: true },
        );
        this._adVideo.addEventListener('timeupdate', this._onAdProgress);
        this._adVideo.addEventListener('pause', this._onAdPause);
        this._adVideo.addEventListener('volumechange', this._onAdVolumeChange);
        this._adVideo.addEventListener('play', this._onAdPlay);
        document.addEventListener(
          'fullscreenchange',
          this._onAdFullscreenChange,
        );
        this._adOverlay.addEventListener('click', this._onAdClick);

        // Play immediately to preserve user gesture context (critical for Safari)
        this._adVideo.play()
          .then(() => {
            clearTimeout(this._adLoadTimeout);
            if (this._skipAllowed) {
              clearTimeout(this._skipButtonTimeout);
              this._skipButtonTimeout = setTimeout(() => {
                if (this._adOverlay.style.display === 'block') {
                  this._skipButton.style.display = 'block';
                }
              }, this._skipDelay);
            }
          })
          .catch((err) => {
            this._onAdError(err);
          });

        // Timeout for ad loading failure
        this._adLoadTimeout = setTimeout(() => {
          if (
            this._adVideo.readyState === 0 && this._adVideo.networkState <= 1
          ) {
            this._onAdError(new Error('Ad failed to load within timeout'));
          }
        }, this._adTimeout || 10000);
      });
    }

    _removeAdVideoListeners() {
      this._adVideo.removeEventListener('canplaythrough', this._onAdCanPlay);
      this._adVideo.removeEventListener('ended', this._onAdEnded);
      this._adVideo.removeEventListener('error', this._onAdError);
      this._adVideo.removeEventListener('playing', this._onAdPlaying);
      this._adVideo.removeEventListener(
        'loadedmetadata',
        this._onAdLoadedMetadata,
      );
      this._adVideo.removeEventListener('timeupdate', this._onAdProgress);
      this._adVideo.removeEventListener('pause', this._onAdPause);
      this._adVideo.removeEventListener('volumechange', this._onAdVolumeChange);
      this._adVideo.removeEventListener('play', this._onAdPlay);
      document.removeEventListener(
        'fullscreenchange',
        this._onAdFullscreenChange,
      );
      this._adOverlay.removeEventListener('click', this._onAdClick);
    }

    // =========================================================================
    // Ad Video Event Handlers
    // =========================================================================

    _onAdCanPlay() {
      clearTimeout(this._adLoadTimeout);
    }

    _onAdEnded() {
      this._adTracker?.track('complete');
      this._adResolve?.();
    }

    _onAdError(error) {
      console.warn('VAST: Ad error', error);
      this._adReject?.(error);
    }

    _onAdPlaying() {
      clearTimeout(this._adLoadTimeout);
    }

    _onAdLoadedMetadata() {
      clearTimeout(this._adLoadTimeout);
    }

    _onAdProgress() {
      this._updateProgress();

      if (this._adVideo.readyState >= 2) {
        clearTimeout(this._adLoadTimeout);
      }

      if (!this._adTracker || !this._adVideo.duration) return;

      const ct = this._adVideo.currentTime;
      const dur = this._adVideo.duration;

      if (!this._quartilesFired.start && ct > 0) {
        this._adTracker.track('start');
        this._quartilesFired.start = true;
      }
      if (!this._quartilesFired.firstQuartile && ct >= dur * 0.25) {
        this._adTracker.track('firstQuartile');
        this._quartilesFired.firstQuartile = true;
      }
      if (!this._quartilesFired.midpoint && ct >= dur * 0.5) {
        this._adTracker.track('midpoint');
        this._quartilesFired.midpoint = true;
      }
      if (!this._quartilesFired.thirdQuartile && ct >= dur * 0.75) {
        this._adTracker.track('thirdQuartile');
        this._quartilesFired.thirdQuartile = true;
      }
    }

    _onAdVolumeChange() {
      if (!this._adTracker) return;
      if (this._adVideo.muted || this._adVideo.volume === 0) {
        this._adTracker.track('mute');
      } else {
        this._adTracker.track('unmute');
      }
    }

    _onAdPause() {
      if (this._adTracker && !this._adVideo.ended) {
        this._adTracker.track('pause');
      }
      // Auto-resume - ads should not be pausable
      if (!this._adVideo.ended && this._adVideo.paused) {
        this._adVideo.play().catch(() => {});
      }
    }

    _onAdPlay() {
      if (!this._adTracker) return;
      this._adTracker.track('resume');
    }

    _onAdFullscreenChange() {
      if (!this._adTracker) return;
      if (
        document.fullscreenElement === this._adVideo ||
        document.fullscreenElement === this._adOverlay
      ) {
        this._adTracker.track('fullscreen');
      } else {
        this._adTracker.track('exitFullscreen');
      }
    }

    _onAdClick(e) {
      if (!this._adClickThroughUrl || !this._adClickThroughUrl.url) return;
      if (
        this._adOverlay.style.display === 'block' &&
        e.target !== this._skipButton
      ) {
        this._adTracker?.track('click');
        globalThis.open(this._adClickThroughUrl.url, '_blank');
      }
    }

    _onGoogleWhyThisAdClick = (e) => {
      e.stopPropagation();
      if (
        this._googleWhyThisAdIconData &&
        this._googleWhyThisAdIconData.clickThrough
      ) {
        globalThis.open(this._googleWhyThisAdIconData.clickThrough, '_blank');
      }
    };

    // =========================================================================
    // Cleanup and Resume
    // =========================================================================

    _cleanupAd() {
      this._adsPlayed = true;
      this._unlockPoster();
      this._hideAdOverlay();
      this._adTimeDisplay.textContent = '';
      this.dispatchEvent(new CustomEvent('adsend'));

      this._removeAdVideoListeners();
      clearTimeout(this._adLoadTimeout);

      this._adVideo.pause();
      this._adVideo.src = '';
      this._adVideo.load();
      this._adVideo.remove();

      // Restore muted state immediately
      if (this._originalMuted !== undefined) {
        this.muted = this._originalMuted;
      }

      // Resume main video playback (currentTime restored inside _resumeMainVideo)
      this._resumeMainVideo();

      // Clean up GoogleWhyThisAd icon
      if (this._googleWhyThisAdIcon) {
        this._googleWhyThisAdIcon.removeEventListener(
          'click',
          this._onGoogleWhyThisAdClick,
        );
        this._googleWhyThisAdIcon.remove();
        this._googleWhyThisAdIcon = null;
      }
    }

    _resumeMainVideo() {
      const needsReload = this._originalSrc &&
        this.src &&
        this._originalSrc !== this.src;
      const savedCurrentTime = this._originalCurrentTime;

      if (needsReload) {
        this.src = this._originalSrc;
        this.load();
        const onCanPlay = () => {
          this.removeAttribute('poster');
          this.style.display = 'block';
          this.style.visibility = 'visible';
          // Restore position after reload completes
          if (savedCurrentTime > 0) {
            this.currentTime = savedCurrentTime;
          }
          setTimeout(() => this.play().catch(() => {}), 100);
          this.removeEventListener('canplaythrough', onCanPlay);
          this.removeEventListener('loadeddata', onCanPlay);
          this.removeEventListener('error', onError);
        };
        const onError = () => {
          this.removeEventListener('canplaythrough', onCanPlay);
          this.removeEventListener('loadeddata', onCanPlay);
          this.removeEventListener('error', onError);
        };
        this.addEventListener('canplaythrough', onCanPlay, { once: true });
        this.addEventListener('loadeddata', onCanPlay, { once: true });
        this.addEventListener('error', onError, { once: true });
      } else if (this.readyState === 0 || this.networkState === 0) {
        // Video in broken state - force reload
        this.load();
        const onCanPlay = () => {
          // Restore position after reload completes
          if (savedCurrentTime > 0) {
            this.currentTime = savedCurrentTime;
          }
          this.play().catch(() => {});
          this.removeEventListener('canplaythrough', onCanPlay);
          this.removeEventListener('loadeddata', onCanPlay);
        };
        this.addEventListener('canplaythrough', onCanPlay, { once: true });
        this.addEventListener('loadeddata', onCanPlay, { once: true });
      } else {
        // No reload needed - restore position immediately
        if (savedCurrentTime > 0) {
          this.currentTime = savedCurrentTime;
        }
        this.play().catch(() => {});
      }
    }
  };
}
